<template>
  <div class="widget w-alerts-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header
      hideLinkModeReceive
      fieldsConfigManager="advanced"
      optimizeColumns
      printView
      alertSoundOption
      :wid="wid"
      @optimize-columns="autoSizeColumns"
    >
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <c-widget-control-bar>
      <c-widget-control-bar-group inline>
        <v-form-input
          type="text"
          :disabled="hasError"
          class="ml-1 mr-2 mb-1"
          autocomplete="off"
          :placeholder="i18n('messages.placeholder-search')"
          v-model="searchInput"
        />
        <v-form-group :label="i18n('template', 'globalLabels')" label-cols="auto">
          <v-form-select
            :options="fieldsConfigNameOptions"
            v-model="controlFieldsConfigName"
            :disabled="hasError"
            :aria-label="i18n('template', 'globalLabels') + ' ' + $t('white-label.globalLabels.screenReaderTemplate')"
          />
        </v-form-group>
      </c-widget-control-bar-group>
      <c-widget-control-bar-group inline>
        <v-button class="mb-1" @click="onCreateNewAlertClicked" :disabled="hasError" @keydown="skipEmptyHeaders">
          <font-awesome-icon :icon="['fas', 'plus']" /> {{ i18n('header.createAlert') }}
        </v-button>
      </c-widget-control-bar-group>
    </c-widget-control-bar>

    <div class="widget-body" @keydown="onKeyDown">
      <c-loader v-if="isLoading && !hasError" />
      <c-streaming-error :show="hasStreamingError" />
      <ag-grid-vue
        v-if="!hasError"
        v-show="!isLoading"
        class="c-data-grid ag-theme-balham-dark"
        animateRows
        :asyncTransactionWaitMillis="gridUpdateInterval"
        immutableData
        :columnDefs="columnDefs"
        :context="this"
        :defaultColDef="defaultColumnDef"
        :gridOptions="gridOptions"
        :rowHeight="agGridRowHeight"
        preventDefaultOnContextMenu
        rowSelection="multiple"
        suppressFieldDotNotation
        tooltipShowDelay="0"
        ensureDomOrder
        suppressContextMenu
        suppressDragLeaveHidesColumns
        :suppressColumnVirtualisation="isAccessibilityEnabled"
        @cell-clicked="onCellClicked"
        @cell-double-clicked="onCellDoubleClicked"
        @cell-key-down="onCellKeyDown"
        @column-moved="onColMoved"
        @drag-stopped="onGridDragEnd"
        @grid-ready="onGridReady"
        @row-selected="onRowSelected"
        @sort-changed="onTableSort"
        @cell-focused="onCellFocused"
        @column-resized="onColResized"
        @new-columns-loaded="ev => setColumnState(ev.columnApi)"
        @async-transactions-flushed="onAsyncTransactionsFlushed"
      />
      <v-alert v-if="hasError" align="center" class="m-3" show variant="light">
        {{ i18n('messages.error') }}
      </v-alert>
    </div>
  </div>
</template>

<script>
import CLoader from '@custom/CLoader';
import CStreamingError from '@custom/CStreamingError';
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetHeaderButton from '@custom/widgetContainer/CWidgetHeaderButton';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import CWidgetControlBarGroup from '@custom/widgetContainer/CWidgetControlBarGroup';
import { API } from '@/state/api';
import { AgGridVue } from 'ag-grid-vue';
import moment from 'moment';
import streamableFidsFilter from '@/utils/streamableFids';
import widgetCommons from '@mixins/widgetCommons';
import agGridCommons from '@mixins/agGridCommons';
import AlertsReactivateButtonRenderer from './alertsReactivateButtonRenderer';
import { GenericTickerRenderer, RowButtonRenderer } from '@custom/dataGrid/renderers';
import { getStatusText } from '@utils/alerts';

import { library as fontAwesomeLibrary } from '@fortawesome/fontawesome-svg-core';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
fontAwesomeLibrary.add(faPlus);

export default {
  name: 'w-alerts-widget',
  mixins: [widgetCommons, agGridCommons],
  components: {
    CLoader,
    CStreamingError,
    CWidgetHeader,
    CWidgetHeaderButton,
    CWidgetControlBar,
    CWidgetControlBarGroup,
    AgGridVue,
    RowButtonRenderer,
    AlertsReactivateButtonRenderer,
    GenericTickerRenderer
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      hasStreamingError: false,
      subscriptionId: null,
      gridOptions: {
        getRowNodeId(data) {
          return data['triggerId'];
        },
        isExternalFilterPresent: () => true,
        doesExternalFilterPass: this.onFilter,
        columnTypes: { actionable: {} },
        tabToNextHeader: this.tabToNextHeader,
        tabToNextCell: this.tabToNextCell
      },
      alertsModel: {},
      quoteData: {},
      searchInput: '',
      dateFormat: 'MM/DD/YYYY',
      columnsWithControls: ['action', 'alert.reactivate', 'q._BID', 'q._ASK'],
      tableFieldsPrepend: ['action', 'alert.reactivate']
    };
  },
  watch: {
    alerts: {
      handler() {
        this.fetch();
      },
      deep: true,
      immediate: true
    },
    searchInput: {
      handler() {
        if (this.gridOptions.api) {
          this.gridOptions.api.onFilterChanged();
        }
      }
    }
  },
  computed: {
    alerts() {
      return this.$store.state.alerts.items;
    },
    extendedColDefs() {
      return {
        action: {
          cellRenderer: 'RowButtonRenderer',
          cellRendererParams: params => {
            const disabledActions =
              params.data.triggerStatus !== 'BRO' ? ['alert', 'edit'] : ['alert', 'edit', 'editAlert'];
            return { disabledActions };
          }
        },
        'alert.reactivate': {
          cellClass: ['reactivate-btn-cell', 'actionable-cell'],
          cellRenderer: 'AlertsReactivateButtonRenderer',
          cellRendererParams: params => {
            const enabled = params.data.triggerStatus === 'SNT';
            const statusText = this.i18n(`srOnly.${enabled ? 'enabled' : 'disabled'}`, 'widgets.alerts');
            const srText = this.i18n('srOnly.reactivate', 'widgets.alerts', [statusText]);
            return {
              enabled,
              srText
            };
          },
          width: 30,
          maxWidth: 30,
          pinned: true,
          lockPosition: true,
          sortable: false,
          type: ['actionable'],
          tooltipValueGetter: () => this.i18n('tooltip.reactivate', 'widgets.alerts')
        },
        'alert.triggerStatus': {
          field: 'triggerStatus',
          width: 80,
          pinned: true,
          lockPosition: true,
          sortable: true,
          valueFormatter: params => this.i18n('table.stati.' + getStatusText(params.value, params.data.expirationDate))
        },
        'q._TCR': {
          width: 60,
          cellRenderer: 'GenericTickerRenderer',
          cellRendererParams: params => {
            return { ticker: params.data['q._TCR'], tradeHaltValue: params.data['q.TRD_STATUS'] };
          }
        },
        'alert.triggerId': {
          field: 'triggerId',
          hide: true
        },
        'alert.infoText': {
          field: 'infoText',
          valueFormatter: params => (params.data.infoText ? params.data.infoText : '-')
        },
        'alert.triggerType': {
          field: 'triggerType',
          cellClass: 'text-left',
          width: 150,
          type: ['actionable'],
          valueGetter: params =>
            this.getFormattedCondition(
              params.data.triggerFid,
              params.data.triggerType,
              this.$f(params.data.triggerValue, 'float')
            ),
          tooltipValueGetter: () => this.i18n('tooltip.doubleClick', 'widgets.alerts')
        },
        'alert.triggerMode': {
          field: 'triggerMode',
          width: 100,
          sortable: true,
          valueGetter: params => this.getTriggerModeText(params.data.triggerType, params.data.triggerStatus)
        },
        'alert.current': {
          field: 'current',
          valueFormatter: params => this.$f(params.value, 'float.price')
        },
        'alert.change': {
          field: 'change',
          valueFormatter: params => this.$f(params.value, 'float.price')
        },
        'alert.expirationDate': {
          field: 'expirationDate',
          maxWidth: 80,
          valueFormatter: params => this.getFormattedDate(params.value),
          type: ['actionable'],
          tooltipValueGetter: () => this.i18n('tooltip.doubleClick', 'widgets.alerts')
        },
        'alert.triggerDate': {
          field: 'triggerDate',
          maxWidth: 90,
          valueFormatter: params => this.getFormattedDate(params.data.triggerDate ? params.data.triggerDate : '')
        }
      };
    }
  },
  methods: {
    onCreateNewAlertClicked() {
      this.$store.dispatch('alerts/openAlertDialog', {
        fromRIC: '',
        wid: this.wid
      });
    },
    onRefreshAlertClicked(triggerId) {
      const alertToRefresh = this.alerts.find(a => a.triggerId === triggerId);

      const alert = {
        ...alertToRefresh,
        triggerStatus: 'ACT'
      };

      this.$store.dispatch('alerts/updateAlert', alert);
    },
    onRowDelete(params) {
      this.$store.dispatch('alerts/deleteAlert', { triggerIds: [params.data.triggerId] });
    },
    onCellClicked(params) {
      if (params.colDef.field === 'alert.reactivate' && params.data.triggerStatus === 'SNT') {
        this.onRefreshAlertClicked(params.data.triggerId);
      }
    },
    onCellDoubleClicked(params) {
      if (params.colDef.field === 'triggerType' || params.colDef.field === 'expirationDate') {
        this.onRowEditAlert(params);
      }
    },
    onRowSelected(params) {
      this.updateLink(params);
    },
    onRowEditAlert(params) {
      if (params.data.triggerStatus !== 'BRO') {
        this.$store.dispatch('alerts/openAlertDialog', {
          triggerId: params.data.triggerId,
          wid: this.wid,
          triggeredFrom: params.colDef && params.colDef.field ? params.colDef.field : ''
        });
      }
    },
    onFilter(params) {
      const ticker = params.data.ric;
      return ticker.toLowerCase().startsWith(this.searchInput.toLowerCase());
    },
    onKeyDown(ev) {
      const key = ev.code || ev.key;

      if (key === 'Delete' || key === 'Backspace') {
        const isInputInFocus = ['input', 'select'].indexOf(document.activeElement.tagName.toLowerCase()) !== -1;
        if (isInputInFocus) return;

        const nodes = this.gridOptions.api.getSelectedNodes();
        if (!nodes.length) return;
        const triggerIds = nodes.map(node => node.data.triggerId);
        this.$store.dispatch('alerts/deleteAlert', { triggerIds });
      }
    },
    skipEmptyHeaders(event) {
      const key = event.code || event.key;
      if (key === 'Tab' && !event.shiftKey) {
        const firstCol = this.getFirstNonActionColumn();
        if (firstCol) {
          //focusing first header element when tabbing from outside to ag-grid requires this
          const nextElement = document.querySelector(`div[col-id="${firstCol.colId}"]`);
          nextElement.focus();
          event.preventDefault();
        }
      }
    },
    getFirstNonActionColumn() {
      return this.gridOptions.columnApi
        .getAllDisplayedColumns()
        .find(col => !['action', 'alert.reactivate'].includes(col.colId));
    },
    getFormattedCondition(triggerFid, triggerType, triggerValue = '') {
      let fid = triggerFid.startsWith('_') ? triggerFid : `_${triggerFid}`;
      fid = this.i18n(`dialogs.options.triggerFid.${fid}`, 'widgets.alerts');

      let type = triggerType.includes('CROSS') ? 'CROSS' : triggerType;
      type = this.i18n(`dialogs.options.triggerType.${type}`, 'widgets.alerts');

      return `${fid} ${type} ${triggerValue}`;
    },
    getTriggerModeText(triggerType, triggerStatus) {
      let triggerMode = 'once';
      if (triggerStatus === 'EXP') {
        triggerMode = 'disabled';
      } else if (triggerType.startsWith('RCROSS')) {
        triggerMode = 'onceDay';
      }
      return this.i18n(`dialogs.options.triggerMode.${triggerMode}`, 'widgets.alerts');
    },
    streamingUnsubscribe() {
      if (this.subscriptionId !== null) {
        API.Streaming.Quote.unsubscribe(this.subscriptionId);
        this.subscriptionId = null;
      }
    },
    streamingSubscribe(fids, rics) {
      this.subscriptionId = API.Streaming.Quote.subscribe(
        {
          rics,
          fids: streamableFidsFilter(fids)
        },
        // Subscribe Callback
        this.handleSubscription,
        // Snapshot Callback
        this.handleSnapshot,
        // Update Callback
        this.handleUpdate
      );
      this.ulStreamingSubscription(rics);
    },
    createQuotedata(data) {
      let result = {};
      data.forEach(d => {
        result[d['q.RIC']] = d;
      });
      return result;
    },
    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    handleSnapshot(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming snapshot error', error);
        return;
      }
      this.hasStreamingError = false;

      this.handleUpdate(response, error);
    },
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }

      if (this.gridOptions && this.gridOptions.api) {
        this.hasStreamingError = false;

        response = response.payload[0];

        try {
          if (!this.quoteData[response.ric]) return;

          const ric = response.ric;
          const data = response.data;

          const updateRows = [];

          const existingData = this.quoteData[ric];

          this.quoteData[ric] = Object.assign(existingData, data);

          // for all alerts that have this ric -> update row in AG-Grid
          this.alertsModel[ric].forEach(alert => {
            const existingRow = this.gridOptions.api.getRowNode(alert.triggerId);

            const hydratedData = this.hydrateData(response.data, existingRow.data);

            hydratedData.current = hydratedData[`q.${alert.triggerFid}`];
            hydratedData.change = parseFloat(hydratedData[`q.${alert.triggerFid}`]) - parseFloat(alert.triggerValue);

            const updateRow = Object.assign(alert, hydratedData);

            updateRows.push(updateRow);
          });

          // update row
          this.dataGridUpdate(updateRows);
        } catch (e) {
          console.error(e);
        }
      }
    },
    dataGridSet(rows) {
      if (this.gridOptions.api) this.gridOptions.api.setRowData(rows);
    },
    dataGridUpdate(updateData) {
      if (this.gridOptions && this.gridOptions.api) this.gridOptions.api.applyTransactionAsync({ update: updateData });
    },
    createRows() {
      const rows = [];
      this.alerts.forEach(a => {
        // create enhanced row (alert + quoteData)
        const hydratedQuoteData =
          this.quoteData[a.ric] !== undefined ? this.hydrateData(this.quoteData[a.ric], {}) : {};
        const row = {
          ...hydratedQuoteData,
          ...a
        };

        // In case of alert being an expired option -> set some values manually as there is no quote data for it
        if (!Object.keys(hydratedQuoteData).length) {
          row.triggerStatus = 'OPTION-EXP';
          row['q._TCR'] = a.ric;
        }

        row.current = row[`q.${a.triggerFid}`];
        row.change = parseFloat(row[`q.${a.triggerFid}`]) - parseFloat(a.triggerValue);

        rows.push(row);

        // create alertsModel
        if (!this.alertsModel[a.ric]) {
          this.alertsModel[a.ric] = [];
        }
        this.alertsModel[a.ric].push({ ...a });
      });

      return rows;
    },
    // ------------ Helpers -------------
    getFormattedDate(dateString) {
      return dateString !== undefined && dateString !== '' ? moment(dateString).format(this.dateFormat) : dateString;
    },
    getTimezonedISOString(expirationDate) {
      return new Date(expirationDate.getTime() - new Date().getTimezoneOffset() * 60000).toISOString().split('.')[0];
    },
    getSnapshot(fids, rics) {
      return API.Rest.get(`/quote/info?rics=${rics}&fids=${fids}`)
        .then(response => {
          Object.assign(
            this.formats,
            Object.keys(response.data.metadata).reduce((acc, ric) => {
              acc[ric] = response.data.metadata[ric].type;
              return acc;
            }, {})
          );
          this.quoteData = this.createQuotedata(response.data.data);
          const rowData = this.createRows();
          this.dataGridSet(rowData);
          if (this.gridOptions && this.gridOptions.api)
            this.gridOptions.api.refreshCells({ force: true, columns: ['alert.reactivate', 'action'] });
          this.isLoading = false;
        })
        .catch(err => {
          console.error('Snapshot fetch failed:', err);
          this.hasError = true;
        });
    },
    fetch() {
      this.hasError = false;
      this.hasStreamingError = false;
      this.quoteData = {};
      this.alertsModel = {};

      this.streamingUnsubscribe();

      if (this.alerts.length) {
        const fids = ['q._TYPE', 'q._NETCHNG_1', ...this.requestFids];
        const rics = [];

        this.alerts.forEach(d => {
          if (!fids.includes(`q.${d.triggerFid}`)) {
            fids.push(`q.${d.triggerFid}`);
          }
          if (!rics.includes(d.ric)) {
            rics.push(d.ric);
          }
        });

        this.getSnapshot(fids.filter(f => f.includes('.') && !f.startsWith('alert.')), rics)
          .catch(err => {
            console.error('Streaming subscribe failed:', err);
            this.hasError = true;
          })
          .then(this.streamingConnect)
          .then(this.streamingUnsubscribe)
          .then(() => this.streamingSubscribe(fids, rics.filter(r => this.quoteData[r])));
      } else {
        setTimeout(() => {
          this.dataGridSet([]);
          this.isLoading = false;
        }, 50);
      }
    },
    onCellKeyDown(params) {
      const isColumnActionable = this.columnsWithControls.includes(params.colDef.colId);
      if (params.event.key === 'Enter') {
        if (params.data.triggerStatus === 'SNT' && params.colDef.field === 'alert.reactivate') {
          this.onRefreshAlertClicked(params.data.triggerId);
        } else if (params.data.triggerStatus !== 'BRO' && !isColumnActionable) {
          this.$store.dispatch('alerts/openAlertDialog', {
            triggerId: params.data.triggerId,
            wid: this.wid
          });
        }
      }

      if (
        (params.colDef.field === 'triggerType' || params.colDef.field === 'expirationDate') &&
        (params.event.keyCode === 32 || params.event.keyCode === 13)
      ) {
        this.onRowEditAlert(params);
      }
    },
    onGridReady(ev) {
      this.onTableReady(ev, this.i18n('widget-title'));
    }
  },
  beforeMount() {
    this.defaultColumnDef.suppressHeaderKeyboardEvent = params => {
      const key = params.event.code || params.event.key;
      const firstCol = this.getFirstNonActionColumn();
      const shiftTab = key == 'Tab' && params.event.shiftKey;
      return params.column.colId === firstCol.colId && (shiftTab || key === 'ArrowLeft');
    };
    this.defaultColumnDef.suppressKeyboardEvent = params => {
      const key = params.event.code || params.event.key;
      if (
        key === 'ArrowUp' &&
        ['action', 'alert.reactivate'].includes(params.column.colId) &&
        params.node.rowIndex === 0
      ) {
        return true;
      }
      return false;
    };
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
    if (this.gridOptions) this.gridOptions = null;
    if (this.gridApi) this.gridApi = null;
  }
};
</script>
<style lang="scss">
@import '~@/styles/vendor/ag-grid';
@import '~@/styles/widgets/alerts-widget';
</style>
